import { useCallback, useMemo, useState, useRef, Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import TextField from "@/components/form/textField/TextField"

import { usePdfBuilderStore, selectSelectedItem } from "@/stores/pdfBuilder"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import type { CanvasItem, ChartOptions, DataSourceConfig } from "@/types/pdfBuilder"
import type { ActivityParticipatedPlayerType } from "@/types/activity"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import SessionSelector from "@/app/report/components/filter/sessionSelector/SessionSelector"

import SummaryOptions from "./options/SummaryOptions"
import BarChartOptions from "./options/BarChartOptions"
import TableOptions from "./options/TableOptions"
import MapOptions from "./options/MapOptions"
import ComparisonOptions from "./options/ComparisonOptions"
import SessionDetailsOptions from "./options/SessionDetailsOptions"
import ScatterOptions from "./options/ScatterOptions"
import { isGroupSharedOptionField } from "@/app/report/pdf/builder/utils/paletteItems"
import type { ChartOptionsPanelProps } from "./options/types"
import { LayoutModeSelector } from "./options/SharedWidgets"

/** 자기 Layout(분할 설정)을 이미 가진 컴포넌트 — 공용 Layout 을 또 넣지 않는다 (2026-10-01 결정) */
const TYPES_WITH_OWN_LAYOUT = new Set(["table", "map", "sessionDetails"])

export default function PropertyPanel() {
  const { t } = useTranslation()
  const [sessionOpen, setSessionOpen] = useState(false)

  const updateItem = usePdfBuilderStore((s) => s.updateItem)
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  const filterSessionIds = usePdfBuilderStore((s) => s.filterSessionIds)
  const selectedItem = usePdfBuilderStore(selectSelectedItem)
  const { metricList, activityDetail, filterPlayers, resolveItemPlayerIds, reportMode } =
    usePdfDataContext()
  const translateKey = useTranslateMetricKey()

  const [playerOpen, setPlayerOpen] = useState(false)

  const handleTitleChange = useCallback(
    (value: string) => {
      const state = usePdfBuilderStore.getState()
      if (!state.currentPageId || !state.selectedItemId) return
      const item = selectSelectedItem(state)
      updateItem(state.currentPageId, state.selectedItemId, {
        options: {
          ...item?.options,
          title: value,
        },
      })
    },
    [updateItem],
  )

  // 같은 groupId 로 묶인 아이템(Session Details 네 차트)은 설정을 공유한다 — 선택한 아이템과 그 묶음 전체에 적용
  const forEachGroupItem = useCallback(
    (state: ReturnType<typeof usePdfBuilderStore.getState>, fn: (target: CanvasItem) => void) => {
      const item = selectSelectedItem(state)
      if (!item) return
      const groupId = item.options.groupId
      const page = state.pages.find((p) => p.id === state.currentPageId)
      const targets = groupId
        ? (page?.items.filter((p) => p.options.groupId === groupId) ?? [item])
        : [item]
      targets.forEach(fn)
    },
    [],
  )

  const handleDataSourceChange = useCallback(
    (field: keyof DataSourceConfig, value: string | number | string[] | number[]) => {
      const state = usePdfBuilderStore.getState()
      if (!state.currentPageId || !state.selectedItemId) return
      const pageId = state.currentPageId
      forEachGroupItem(state, (target) =>
        updateItem(pageId, target.id, {
          dataSource: {
            ...target.dataSource,
            [field]: value,
          } as DataSourceConfig,
        }),
      )
    },
    [updateItem, forEachGroupItem],
  )

  const handleOptionChange = useCallback(
    (
      field: keyof ChartOptions,
      value:
        | string
        | number
        | boolean
        | string[]
        | Record<string, string>
        | Record<string, number | null>
        | null,
    ) => {
      const state = usePdfBuilderStore.getState()
      if (!state.currentPageId || !state.selectedItemId) return
      const pageId = state.currentPageId
      const item = selectSelectedItem(state)
      // 제목·보기·자동 크기는 아이템마다 따로, 나머지는 묶음 전체에
      if (!isGroupSharedOptionField(field)) {
        updateItem(pageId, state.selectedItemId, {
          options: { ...item?.options, [field]: value },
        })

        return
      }
      forEachGroupItem(state, (target) =>
        updateItem(pageId, target.id, {
          options: { ...target.options, [field]: value },
        }),
      )
    },
    [updateItem, forEachGroupItem],
  )

  const metricOptions = useMemo(
    () => metricList.map((m) => ({ value: m.key, label: translateKey(m.key) })),
    [metricList, translateKey],
  )

  const itemSessionIds = filterSessionIds

  // Data Source 선수 후보 = Report Filters 에서 선택된 선수. 하위 선택은 상위 필터를 벗어나지 않는다.
  const selectablePlayers: ActivityParticipatedPlayerType[] = filterPlayers

  // Per-item player IDs, narrowed to the filter (default: all filter players)
  const itemPlayerIds = useMemo(
    () => resolveItemPlayerIds(selectedItem?.dataSource.playerIds),
    [selectedItem?.dataSource.playerIds, resolveItemPlayerIds],
  )

  // 선택된 선수는 아이템의 dataSource.playerIds 하나만을 출처로 삼는다 (로컬 사본 없음).
  // PlayerSelector 가 선수 객체 배열 + useState 형태의 setter 를 요구하므로 여기서 변환만 한다.
  const localPlayerList = useMemo(() => {
    const ids = new Set(itemPlayerIds)

    return selectablePlayers.filter((p) => ids.has(p.idfPlayer))
  }, [selectablePlayers, itemPlayerIds])

  const localPlayerListRef = useRef(localPlayerList)
  localPlayerListRef.current = localPlayerList

  const setLocalPlayerList: Dispatch<SetStateAction<ActivityParticipatedPlayerType[]>> =
    useCallback(
      (action) => {
        const prev = localPlayerListRef.current
        const next = typeof action === "function" ? action(prev) : action
        handleDataSourceChange(
          "playerIds",
          next.map((p) => p.idfPlayer),
        )
      },
      [handleDataSourceChange],
    )

  // Shared data for option panels
  const availableSessions = activityDetail?.sessions ?? []

  // Action Map 의 Data Source 세션 — Report Filters 세션 안에서 고른다. 비어 있으면 전부
  const selectableSessions = availableSessions.filter((s) =>
    filterSessionIds.includes(s.idfSession),
  )
  const pickedSessionIds = (selectedItem?.dataSource.sessionIds ?? []).filter((id) =>
    filterSessionIds.includes(id),
  )
  // 손대지 않았으면 필터 전체가 고른 것처럼, 한 번이라도 고르면 그 목록 그대로(전부 해제하면 빈 상태) (2026-10-01 요청)
  const effectiveSessionIds =
    selectedItem?.dataSource.sessionIds === undefined ? filterSessionIds : pickedSessionIds
  // Action Map·Session Details 는 세션도 고른다 (2026-10-01 요청: Session Details 도 Data Source 에서)
  const showSessionPicker =
    (selectedItem?.chartType === "map" || selectedItem?.chartType === "sessionDetails") &&
    reportMode === ACTIVITY_SESSION_TYPE.SESSION &&
    selectableSessions.length > 0

  const commonProps: ChartOptionsPanelProps = {
    item: selectedItem!,
    onDataSourceChange: handleDataSourceChange,
    onOptionChange: handleOptionChange,
    availableSessions: availableSessions.map((s) => ({
      idfSession: s.idfSession,
      name: s.name,
    })),
    availablePlayers: selectablePlayers.map((p) => ({
      idfPlayer: p.idfPlayer,
      name: p.name,
      uniformNumber: p.uniformNumber,
    })),
    itemSessionIds,
    itemPlayerIds,
    metricOptions,
    translateKey,
  }

  if (!selectedItem) {
    return (
      <div className="PropertyPanel">
        <div className="PropertyPanel__header">
          <h3>{t("properties", "Properties")}</h3>
        </div>
        <div className="PropertyPanel__empty">
          <p>{t("selectComponent", "Select a component to edit its properties")}</p>
        </div>
      </div>
    )
  }

  // 액티비티 정보와 스페이서는 선수 데이터를 쓰지 않는다
  const showDataSource =
    selectedItem.chartType !== "activityInfo" && selectedItem.chartType !== "spacer"

  const renderChartSpecificOptions = () => {
    switch (selectedItem.chartType) {
      case "activityInfo":
        return null
      case "summary":
        return <SummaryOptions {...commonProps} />
      case "bar":
      case "line":
      case "combo":
      case "activityGraph":
      case "point":
        return <BarChartOptions {...commonProps} />
      case "sessionDetails":
        return <SessionDetailsOptions {...commonProps} />
      case "scatter":
        return <ScatterOptions {...commonProps} />
      case "table":
        return <TableOptions {...commonProps} />
      case "map":
        return <MapOptions {...commonProps} />
      case "comparison":
        return <ComparisonOptions {...commonProps} />
      default:
        return null
    }
  }

  // 컴포넌트 종류와 상관없이 "Options" 하나로 (2026-10-01 요청)
  const chartTypeLabel = t("chartOptions", "Options")

  return (
    <div className="PropertyPanel">
      <div className="PropertyPanel__header">
        <h3>{t("properties", "Properties")}</h3>
      </div>
      <div className="PropertyPanel__content">
        {/* General Section */}
        {selectedItem.chartType !== "activityInfo" && (
          <div className="PropertyPanel__section">
            <h4 className="PropertyPanel__section-title">{t("general", "General")}</h4>
            <div className="PropertyPanel__field">
              <label>{t("title", "Title")}</label>
              {/* Scatter 제목은 고른 지표에서 자동으로 만든다 — 직접 고치지 않는다 (2026-10-01 요청) */}
              <TextField
                value={selectedItem.options.title || ""}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder={t("enterTitle", "Enter title...")}
                disabled={selectedItem.chartType === "scatter"}
              />
            </div>
          </div>
        )}

        {/* Data Source Section - Report Filters 선수 범위 안에서만 고른다. 필터 선수가 없으면 숨김 */}
        {showDataSource && selectablePlayers.length > 0 && (
          <div className="PropertyPanel__section">
            <h4 className="PropertyPanel__section-title">{t("dataSource", "Data Source")}</h4>
            {/* Action Map 은 세션도 고른다 — Report Filters 세션 안에서, 아무것도 안 고르면 전부 (2026-09-29 개편). 선수 위에 둔다 (2026-10-01 요청) */}
            {showSessionPicker && activityDetail && (
              <div className="PropertyPanel__field">
                <label>{t("session", "Session")}</label>
                {/* Report Filters 의 액티비티/세션 선택기에서 세션 목록만 — 후보는 Report Filters 세션 안으로 좁힌다 */}
                <SessionSelector
                  open={sessionOpen}
                  setOpen={setSessionOpen}
                  hideRadioSelection
                  selectedType={ACTIVITY_SESSION_TYPE.SESSION}
                  setSelectedType={() => undefined}
                  selectedSessionList={effectiveSessionIds}
                  setSelectedSessionList={(ids) => handleDataSourceChange("sessionIds", ids)}
                  detail={{ ...activityDetail, sessions: selectableSessions }}
                />
              </div>
            )}
            <div className="PropertyPanel__field">
              <label>{t("player")}</label>
              <PlayerSelector
                open={playerOpen}
                setOpen={setPlayerOpen}
                selectedActivity={filterActivityId ? [filterActivityId] : []}
                players={selectablePlayers}
                selectedPlayerList={localPlayerList}
                setSelectedPlayerList={setLocalPlayerList}
                paramsVersion={false}
              />
            </div>
          </div>
        )}

        {/* Chart Options Section */}
        {selectedItem.chartType !== "spacer" && (
          <div className="PropertyPanel__section">
            <h4 className="PropertyPanel__section-title">{chartTypeLabel}</h4>
            {renderChartSpecificOptions()}
            {/* 배치 — 모든 컴포넌트 공통, 맨 아래에 둔다 (2026-10-01 요청).
                Table·Action Map·Session Details 는 자기 분할 설정이 Layout 이라 각 패널에 있다 */}
            {!TYPES_WITH_OWN_LAYOUT.has(selectedItem.chartType) && (
              <LayoutModeSelector
                value={selectedItem.options.layoutMode ?? "auto"}
                onChange={(value) => handleOptionChange("layoutMode", value)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
